import {
  EXPERIMENT_STATUS,
  HIPHEN_GREEN,
  MISSION_MONITORING_FLAG_COLORS,
  PLOTMAP_STATUS,
  clientActionReason,
} from "../../constants";

import { CountryFlag } from "../../components/CountryFlag/CountryFlag";
import { FieldMapSVG } from "../../static/svg/FieldMapSVG";
import PropTypes from "prop-types";
import React from "react";
import { UploadCounter } from "./UploadCounter";
import { iso31661 } from "iso-3166";

const countryLabel = (alpha) =>
  iso31661.find((c) => c.alpha3 === alpha)?.name ?? "";

// EXPERIMENT_STATUS carries Bootstrap contextual classes; map them to concrete
// hex so the field-map status can show a coloured dot like the plot-map one.
export const EXPERIMENT_HEX = {
  "bg-dark": "#2f3a40",
  "bg-secondary": "#9aa0a6",
  "bg-info": "#3aa0c8",
  "bg-success": "#8fbf48",
  "bg-primary": "#184456",
};

// One "Field map" / "Plot map" status line: a muted key on the left, a coloured
// dot + status label on the right.
// `icon` is a node, not a class name: the field map has its own SVG asset and
// cannot be expressed as a FontAwesome class.
const StatusRow = ({ icon, label, color, status }) => (
  <div className="cf-status__row">
    <span className="cf-status__key">
      {icon} {label}
    </span>
    <span className="cf-status__val" style={{ color }} title={status}>
      <span className="cf-status__dot" aria-hidden="true" />
      <span className="cf-status__valtext">{status}</span>
    </span>
  </div>
);

StatusRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};

// Monitoring signals for a site: active flags (red) + unread comments (green).
// Shared by the bento tile and the list table so both views read identically.
export const MonitoringSignals = ({ monitoring }) => {
  if (!(monitoring?.flags > 0) && !(monitoring?.comments > 0)) return null;
  return (
    <div className="cf-tile__signals">
      {monitoring.flags > 0 && (
        <span
          className="cf-tile__signal"
          data-tooltip-id="tooltip"
          data-tooltip-content={`${monitoring.flags} active flag${
            monitoring.flags === 1 ? "" : "s"
          }`}
        >
          <i
            className="fa-solid fa-flag"
            style={{ color: MISSION_MONITORING_FLAG_COLORS.RAISED }}
          />{" "}
          {monitoring.flags}
        </span>
      )}
      {monitoring.comments > 0 && (
        <span
          className="cf-tile__signal"
          data-tooltip-id="tooltip"
          data-tooltip-content={`${monitoring.comments} unread comment${
            monitoring.comments === 1 ? "" : "s"
          }`}
        >
          <i
            className="fa-solid fa-comment-dots"
            style={{ color: HIPHEN_GREEN }}
          />{" "}
          {monitoring.comments}
        </span>
      )}
    </div>
  );
};

MonitoringSignals.propTypes = {
  monitoring: PropTypes.shape({
    flags: PropTypes.number,
    comments: PropTypes.number,
  }),
};

// A bento tile for a single site.
export const SiteTile = ({
  site,
  showExperiment,
  showTechnicalName,
  hasResults,
  canEdit,
  uploads,
  monitoring,
  onOpen,
  onEdit,
  onViewResults,
}) => {
  const experiment = EXPERIMENT_STATUS.properties[site.experiment_status];
  const plotmap =
    PLOTMAP_STATUS.properties[
      site.plotmap_status ?? PLOTMAP_STATUS.NOT_DELIVERED
    ];
  // `name` and `display_name` are stored independently; `name` is the internal
  // identifier, shown only to backoffice and admin users. A client has no use
  // for it and reads it as a second, contradictory name for their own site.
  const altName =
    showTechnicalName && site.name && site.name !== site.display_name
      ? site.name
      : null;
  const actionReason = clientActionReason(site);
  const needsAction = actionReason !== null;
  const stop = (event) => event.stopPropagation();

  return (
    <div
      className={
        "cf-tile cf-tile--site" + (needsAction ? " cf-tile--action" : "")
      }
      // Action-required → the left rail turns warning-orange (matches the
      // "N sites require attention" filter); otherwise it carries the plot-map
      // status colour.
      style={{
        "--cf-accent": needsAction ? "#ffa91e" : plotmap?.color ?? "#81b23d",
      }}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(site)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(site);
        }
      }}
    >
      <div className="cf-tile__head">
        <h3
          className="cf-tile__title"
          data-tooltip-id="tooltip"
          data-tooltip-content={site.display_name}
        >
          {site.display_name}
        </h3>
        <div className="cf-tile__head-actions" onClick={stop}>
          {canEdit && (
            <button
              type="button"
              className="cf-iconbtn"
              data-tooltip-id="tooltip"
              data-tooltip-content="Edit site"
              onClick={() => onEdit(site)}
            >
              <i className="fa-solid fa-pencil" />
            </button>
          )}
          {hasResults && (
            <button
              type="button"
              className="btn-icon btn-icon--solid cf-resultbtn"
              data-tooltip-id="tooltip"
              data-tooltip-content="View result on map"
              onClick={() => onViewResults(site)}
            >
              <i className="fa-solid fa-earth-europe" />
            </button>
          )}
        </div>
      </div>
      {altName && (
        <span className="cf-tile__subname" title={altName}>
          {altName}
        </span>
      )}

      <div className="cf-tags">
        <span className={"cf-tag" + (site.crop ? "" : " is-empty")}>
          <i className="fa-solid fa-leaf" />
          <span className="cf-tag__text">{site.crop || "No crop"}</span>
        </span>
        <span className={"cf-tag" + (site.country ? "" : " is-empty")}>
          {site.country ? (
            <CountryFlag alpha={site.country} size="xs" />
          ) : (
            <span className="cf-flag-na" aria-hidden="true">
              ?
            </span>
          )}
          <span className="cf-tag__text">
            {site.country
              ? countryLabel(site.country) || "Unknown"
              : "No country"}
          </span>
        </span>
      </div>

      {actionReason && (
        <span className="cf-tile__reason">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          {actionReason}
        </span>
      )}

      <div className="cf-tile__spacer" />

      {/* Field map / plot map progress */}
      <div className="cf-status">
        {showExperiment && experiment && (
          <StatusRow
            icon={
              <FieldMapSVG
                width="13px"
                height="13px"
                fill="currentColor"
                aria-hidden="true"
              />
            }
            label="Field map"
            color={EXPERIMENT_HEX[experiment.color] ?? "#9aa0a6"}
            status={experiment.label}
          />
        )}
        {plotmap && (
          <StatusRow
            icon={<i className="fa-solid fa-object-group" aria-hidden="true" />}
            label="Plot map"
            color={plotmap.color}
            status={plotmap.label}
          />
        )}
      </div>

      <div className="cf-tile__footer">
        {/* Flags (red) + comments (green) on the left; uploads + results right. */}
        <MonitoringSignals monitoring={monitoring} />
        <div className="cf-tile__footer-right">
          {uploads && <UploadCounter counts={uploads} />}
        </div>
      </div>
    </div>
  );
};

SiteTile.propTypes = {
  site: PropTypes.object.isRequired,
  showExperiment: PropTypes.bool,
  showTechnicalName: PropTypes.bool,
  hasResults: PropTypes.bool,
  canEdit: PropTypes.bool,
  uploads: PropTypes.object,
  monitoring: PropTypes.shape({
    flags: PropTypes.number,
    comments: PropTypes.number,
  }),
  onOpen: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onViewResults: PropTypes.func.isRequired,
};
